import { Head, router, useForm } from "@inertiajs/react"
import { Layers, Search, Plus, Users } from "lucide-react"
import { useMemo, useState } from "react"
import { AdminLayout } from "../components/AdminLayout"
import { SectionTabs } from "../components/SectionTabs"

const userManagementTabs = [
  { label: "People", href: "/app/admin/people" },
  { label: "Groups", href: "/app/admin/groups" },
]
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogFooter,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
  ResponsiveDialogClose,
} from "@/components/ui/responsive-dialog"

function CreateGroupDialog({ open, onOpenChange }) {
  const form = useForm({ name: "", description: "" })

  const handleSubmit = (e) => {
    e.preventDefault()
    form.post("/app/admin/groups", {
      preserveScroll: true,
      onSuccess: () => onOpenChange(false),
    })
  }

  const handleClose = (isOpen) => {
    if (!isOpen) {
      form.reset()
      form.clearErrors()
    }
    onOpenChange(isOpen)
  }

  return (
    <ResponsiveDialog open={open} onOpenChange={handleClose}>
      <ResponsiveDialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle className="font-display text-xl">
              Create Group
            </ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              Create a new coaching group.
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>

          <div className="space-y-4 py-4 px-4 sm:px-0">
            <div className="space-y-2">
              <Label htmlFor="name">
                Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                placeholder="Group name"
                className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
                value={form.data.name}
                onChange={(e) => form.setData("name", e.target.value)}
                required
              />
              {form.errors.name && (
                <p className="text-sm text-red-600">{form.errors.name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Optional description..."
                className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400 min-h-[80px]"
                value={form.data.description}
                onChange={(e) => form.setData("description", e.target.value)}
              />
            </div>
          </div>

          <ResponsiveDialogFooter>
            <ResponsiveDialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </ResponsiveDialogClose>
            <Button
              type="submit"
              variant="gradient"
              disabled={form.processing}
            >
              {form.processing ? "Creating..." : "Create Group"}
            </Button>
          </ResponsiveDialogFooter>
        </form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}

export default function GroupsIndex({ currentUser, groups, availableCoaches = [], pendingCount }) {
  const [searchQuery, setSearchQuery] = useState("")
  const [coachFilter, setCoachFilter] = useState("all")
  const [createOpen, setCreateOpen] = useState(false)

  const filteredGroups = useMemo(() => {
    if (!groups) return []
    const q = searchQuery.trim().toLowerCase()
    return groups.filter((group) => {
      // Coach filter: match when at least one coach's id equals the selection.
      if (coachFilter !== "all") {
        const hasCoach = (group.coaches || []).some(
          (c) => String(c.id) === coachFilter,
        )
        if (!hasCoach) return false
      }

      // Search: match against group name OR any coach name.
      if (q) {
        const inName = group.name.toLowerCase().includes(q)
        const inCoach = (group.coaches || []).some((c) =>
          c.name.toLowerCase().includes(q),
        )
        if (!inName && !inCoach) return false
      }

      return true
    })
  }, [groups, searchQuery, coachFilter])

  return (
    <AdminLayout currentUser={currentUser} pendingCount={pendingCount}>
      <Head title="Groups - Kingdom Factor Admin" />

      <div className="space-y-6">
        <SectionTabs tabs={userManagementTabs} />

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">Groups</h1>
            <p className="text-zinc-500">Manage coaching groups</p>
          </div>
          <Button variant="gradient" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Group
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <Input
              type="search"
              placeholder="Search by group or coach name..."
              className="pl-9 bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Select value={coachFilter} onValueChange={setCoachFilter}>
            <SelectTrigger className="w-full sm:w-64 bg-white border-zinc-300">
              <SelectValue placeholder="All coaches" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All coaches</SelectItem>
              {availableCoaches.map((coach) => (
                <SelectItem key={coach.id} value={String(coach.id)}>
                  {coach.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Groups Table */}
        <Card className="bg-white border-zinc-200">
          <CardContent className="p-0">
            {filteredGroups?.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-200 hover:bg-transparent">
                    <TableHead className="text-zinc-500">Name</TableHead>
                    <TableHead className="text-zinc-500 hidden md:table-cell">Coaches</TableHead>
                    <TableHead className="text-zinc-500 w-24 hidden sm:table-cell">Coach #</TableHead>
                    <TableHead className="text-zinc-500 w-24 hidden sm:table-cell">Members</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredGroups.map((group) => (
                    <TableRow
                      key={group.id}
                      className="cursor-pointer border-zinc-100 hover:bg-zinc-50"
                      onClick={() =>
                        router.visit(`/app/admin/groups/${group.id}`)
                      }
                    >
                      <TableCell className="font-medium text-zinc-900 whitespace-normal">
                        <div className="min-w-0">
                          <p>{group.name}</p>
                          {group.description && (
                            <p className="text-sm text-zinc-400 line-clamp-1">
                              {group.description}
                            </p>
                          )}
                          <p className="text-sm text-zinc-500 truncate md:hidden mt-0.5">
                            {group.coaches && group.coaches.length > 0
                              ? group.coaches.map((c) => c.name).join(", ")
                              : "No coach"}
                          </p>
                          <div className="mt-1.5 flex items-center gap-1.5 sm:hidden">
                            <Badge
                              variant="outline"
                              className="border-kf-red text-kf-red"
                            >
                              <Users className="h-3 w-3 mr-1" />
                              {group.coachesCount}
                            </Badge>
                            <Badge
                              variant="outline"
                              className="border-blue-500 text-blue-500"
                            >
                              <Users className="h-3 w-3 mr-1" />
                              {group.membersCount}
                            </Badge>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-zinc-700 hidden md:table-cell">
                        {group.coaches && group.coaches.length > 0
                          ? group.coaches.map((c) => c.name).join(", ")
                          : <span className="text-zinc-400 italic">No coach</span>}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <Badge
                          variant="outline"
                          className="border-kf-red text-kf-red"
                        >
                          <Users className="h-3 w-3 mr-1" />
                          {group.coachesCount}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <Badge
                          variant="outline"
                          className="border-blue-500 text-blue-500"
                        >
                          <Users className="h-3 w-3 mr-1" />
                          {group.membersCount}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12 text-zinc-400">
                <Layers className="h-12 w-12 mx-auto mb-3 text-zinc-300" />
                <p className="font-medium text-zinc-500">No groups found</p>
                <p className="text-sm">
                  {searchQuery
                    ? "Try a different search term"
                    : "Create your first group to get started"}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <CreateGroupDialog open={createOpen} onOpenChange={setCreateOpen} />
    </AdminLayout>
  )
}
